import { useEffect, useRef, useState } from 'react'
import Image from 'next/image'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Modal from '@mui/material/Modal'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import CloseIcon from '@mui/icons-material/Close'

const imagePreviewModalComponentsProps = {
  backdrop: {
    timeout: 500,
  },
}
const imageWidthRatio = 0.8
const mobileImageHeightRatio = 0.8
const desktopImageHeightRatio = 0.7

const getPreviewSize = (naturalWidth, naturalHeight, isDesktop) => {
  if (typeof window === 'undefined' || !naturalWidth || !naturalHeight) {
    return null
  }

  const widthScale = (window.innerWidth * imageWidthRatio) / naturalWidth
  const imageHeightRatio = isDesktop
    ? desktopImageHeightRatio
    : mobileImageHeightRatio
  const heightScale = (window.innerHeight * imageHeightRatio) / naturalHeight
  const scale = Math.min(widthScale, heightScale)

  return {
    width: Math.round(naturalWidth * scale),
    height: Math.round(naturalHeight * scale),
  }
}

const ImagePreviewModal = ({ open, image, onClose }) => {
  const imageRef = useRef(null)
  const [naturalSize, setNaturalSize] = useState(null)
  const [previewSize, setPreviewSize] = useState(null)
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))

  const updateImageSize = (loadedImage) => {
    if (!loadedImage?.naturalWidth || !loadedImage?.naturalHeight) return

    const loadedNaturalSize = {
      src: image.src,
      width: loadedImage.naturalWidth,
      height: loadedImage.naturalHeight,
    }

    setNaturalSize(loadedNaturalSize)
    setPreviewSize(
      getPreviewSize(
        loadedNaturalSize.width,
        loadedNaturalSize.height,
        isDesktop
      )
    )
  }

  useEffect(() => {
    if (open && imageRef.current?.complete) {
      updateImageSize(imageRef.current)
    }
  }, [image?.src, open])

  useEffect(() => {
    if (!open || naturalSize?.src !== image?.src) {
      setPreviewSize(null)
      return undefined
    }

    const updatePreviewSize = () => {
      setPreviewSize(
        getPreviewSize(naturalSize.width, naturalSize.height, isDesktop)
      )
    }

    updatePreviewSize()
    window.addEventListener('resize', updatePreviewSize)
    return () => window.removeEventListener('resize', updatePreviewSize)
  }, [image?.src, isDesktop, naturalSize, open])

  return (
    <Modal
      open={open}
      onClose={onClose}
      componentsProps={imagePreviewModalComponentsProps}
      aria-label={image?.alt || '圖片預覽'}
    >
      <Box
        position="absolute"
        top={0}
        right={0}
        bottom={0}
        left={0}
        display="flex"
        alignItems="center"
        justifyContent="center"
        py={8}
        px={4}
        onClick={onClose}
        sx={{ outline: 'none' }}
      >
        <Box
          position="relative"
          width={previewSize ? `${previewSize.width}px` : '1px'}
          height={previewSize ? `${previewSize.height}px` : '1px'}
          visibility={previewSize ? 'visible' : 'hidden'}
          onClick={(event) => event.stopPropagation()}
        >
          <IconButton
            aria-label="關閉圖片"
            onClick={onClose}
            sx={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              zIndex: 1,
              width: { xs: '32px', lg: '40px' },
              height: { xs: '32px', lg: '40px' },
              p: { xs: 1.5, lg: 2 },
              bgcolor: 'background.default',
              boxShadow: '0 3px 3px 0 rgb(0 0 0 / 25%)',
              '&:hover': { bgcolor: 'background.default' },
            }}
          >
            <CloseIcon
              sx={{
                color: 'primary.mainPlus2',
                fontSize: { xs: '20px', lg: '24px' },
              }}
            />
          </IconButton>
          {!!image?.src && (
            <Box position="relative" width="100%" height="100%">
              <Image
                ref={imageRef}
                src={image.src}
                alt={image.alt || ''}
                fill
                sizes="80vw"
                style={{ objectFit: 'contain' }}
                onLoad={({ currentTarget }) => updateImageSize(currentTarget)}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Modal>
  )
}

export default ImagePreviewModal
